import './MyWork.scss';
import { useEffect, useRef, useState } from 'react';
import CustomerTab from './CustomerTab';
import SearchResults from './SearchResults';
import {
  BillingInfo,
  COMMANDS,
  ClaimInfo,
  LoadCustomerMessage,
  OpenQuoteMessage,
  PolicyInfo,
  SetCustomerMessage,
  SwitchMeMessage,
} from './Messages';
import { Tab, Tabs } from '@lmig/lmds-react-tabs';
import { CircularProgressSpinner } from '@lmig/sales-ui-circular-progress-spinner';
import { IconClose, IconMyAccount, IconSearch } from '@lmig/lmds-react-icons';
import { useSessionStorage } from 'Utilities/UseStorage';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useUserData } from 'Foundation/User/UserContext';
import QuoteModal from 'Feature/Quoting/QuoteModal';
import { toTitleCase } from 'Utilities/StringFormatter';
import { SearchInput } from '@lmig/lmds-react-alphanumeric-input';

export type MyWorkCustomerTab = {
  message: LoadCustomerMessage | SwitchMeMessage;
  name?: string;
  activeTab: 'Claims' | 'Customer';
  policies?: PolicyInfo[];
  claims?: ClaimInfo[];
  billing?: BillingInfo[];
  updateActiveTab: (activeTab: 'Claims' | 'Customer') => void;
};
export default function MyWork() {
  const [customerTabs, setCustomerTabs] = useSessionStorage<MyWorkCustomerTab[]>(
    'customer-tabs-' + window.location.host,
    [],
  );
  const [activeTab, setActiveTab] = useSessionStorage<number>('active-customer-tab-' + window.location.host, undefined);
  const [switchMeMessage, setSwitchMeMessage] = useState<SwitchMeMessage>();
  const [newTab, setNewTab] = useState<MyWorkCustomerTab>();
  const location = useLocation();
  let navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState<string | null>(
    new URLSearchParams(location.search).get('searchTerm') || new URLSearchParams(location.search).get('keyword'),
  );
  const [seconadarySearchBoxTerm, setSecondarySearchBoxTerm] = useState<string>(searchTerm ?? '');
  const seconadarySearchBoxRef = useRef<HTMLInputElement>(null);
  const [customerId] = useState<string | null>(new URLSearchParams(location.search).get('customerId'));
  const { userData } = useUserData();
  const [quoteModalConfig, setQuoteModalConfig] = useState<{ isOpen: Boolean; defaultState?: string }>({
    isOpen: false,
    defaultState: userData?.state,
  });

  useEffect(() => {
    //this is needed to be able to use client side rendering w mywork and support the back button functionality
    const sitecoreContent = document.querySelector('#content > .row') as HTMLDivElement | null;
    if (sitecoreContent) {
      sitecoreContent.style.display = 'none';
      const handleBackButton = () => {
        console.log(`reloading ${location.pathname}`);
        window.location.reload();
      };
      // Add the event listener when the component mounts
      window.addEventListener('popstate', handleBackButton);
    }
  }, []);

  const quoteModalOnClose = () => {
    setQuoteModalConfig({ isOpen: false });
  };

  const onMessage = (event: MessageEvent) => {
    if (event.data && typeof event.data == 'string') {
      if (event.data.indexOf('[iFrameSizer]') != -1) return null;

      let message: any = JSON.parse(event.data);
      switch (message.command) {
        case COMMANDS.OPENCUSTOMER: //open a new tab
          let newTabMessage: LoadCustomerMessage | SwitchMeMessage = message.data;
          let isClaims = newTabMessage.command == 'loadClaimsUI';
          setNewTab({
            message: newTabMessage,
            activeTab: isClaims ? 'Claims' : 'Customer',
            updateActiveTab: (activeTab: 'Claims' | 'Customer') => updateActiveTab(activeTab),
          });
          break;
        case COMMANDS.SETCUSTOMER: //update tab with customer name
          let setCustomerMessage = message.data as SetCustomerMessage;
          if (setCustomerMessage.contactInfo.name.fullName) {
            setCustomerTabs((customerTabs) => ({
              data: (customerTabs.data ?? []).map((tab: MyWorkCustomerTab) => {
                if (!setCustomerMessage.customerId || tab.message.data.customerId == setCustomerMessage.customerId) {
                  return {
                    ...tab,
                    policies: setCustomerMessage.policyInfos,
                    claims: setCustomerMessage.claimInfos,
                    billing: setCustomerMessage.billingInfos,
                    name: tab.name && tab.name != '-' ? tab.name : setCustomerMessage.contactInfo.name.fullName,
                  };
                } else return tab;
              }),
            }));
          }
          break;
        case COMMANDS.SWITCHME: //change between claims and customerui
          let switchMeMessage = message.data as SwitchMeMessage;
          setSwitchMeMessage(switchMeMessage);
          break;
        case COMMANDS.OPENQUOTES: //open quote modal
          let openQuotesMessage = message.data as OpenQuoteMessage;
          setQuoteModalConfig({ isOpen: true, defaultState: openQuotesMessage.jurisdiction });
          break;
      }
    }
  };

  const onTabChange = (event: any, index: number) => {
    //no not overrite active tab when closing a tab
    setActiveTab({ data: index });
  };

  const closeTab = (event: any, index: number) => {
    if (activeTab == index + 1 || activeTab > (customerTabs ?? []).length - 1)
      //if were closing the tab were on, jump to the last open one
      setActiveTab({ data: (customerTabs ?? []).length - 1 });
    setCustomerTabs((customerTabs) => ({
      data: [...(customerTabs.data ?? []).slice(0, index), ...(customerTabs.data ?? []).slice(index + 1)],
    }));
    event.stopPropagation();
  };

  useEffect(() => {
    if (!searchTerm) focusSecondarySearchBox();
    window.addEventListener('message', onMessage);
  }, []);

  useEffect(() => {
    if (switchMeMessage)
      setCustomerTabs((customerTabs) => ({
        data: (customerTabs.data ?? []).map((tab, index) =>
          index == activeTab - 1 ? { ...tab, activeTab: 'Claims', message: switchMeMessage } : tab,
        ),
      }));
  }, [switchMeMessage]);

  useEffect(() => {
    if (newTab) {
      let existingTab = customerTabs.find((x) => x.message.data.customerId === newTab.message.data.customerId);
      if (existingTab) {
        setActiveTab({
          data: customerTabs.findIndex((x) => x.message.data.customerId === newTab.message.data.customerId) + 1,
        });
      } else {
        setActiveTab({ data: customerTabs.length + 1 });
        setCustomerTabs((customerTabs) => ({ data: [...(customerTabs.data ?? []), newTab] }));
      }
    }
  }, [newTab]);

  useEffect(() => {
    let keyword = searchParams.get('keyword');
    let newSearchTerm: string | null = searchParams.get('searchTerm');

    if (searchParams.has('searchId') || newSearchTerm !== searchTerm || keyword) {
      setActiveTab({ data: 0 });
      searchParams.delete('searchId');
      setSearchParams(searchParams);
      setSecondarySearchBoxTerm(keyword ?? newSearchTerm ?? '');
      newSearchTerm = keyword;
    }

    if (newSearchTerm) setSearchTerm(newSearchTerm);
  }, [location.search]);

  useEffect(() => {
    if (customerId) {
      let newTabMessage: LoadCustomerMessage = {
        command: 'loadCustomer',
        data: { customerId: customerId, customerIdType: searchParams.get('customerIdType') ?? 'MASTER' },
      };
      setNewTab({
        message: newTabMessage,
        activeTab: 'Customer',
        updateActiveTab: (activeTab: 'Claims' | 'Customer') => updateActiveTab(activeTab),
      });
    }
  }, [customerId]);

  useEffect(() => {
    if (activeTab == 0 || activeTab == undefined) document.title = 'Search Results | Customers';
    else {
      let customerName = toTitleCase(customerTabs[activeTab - 1]?.name);
      if (customerName) document.title = `${customerName} | Customers`;
    }
  }, [activeTab, customerTabs]);

  const updateActiveTab = (activeTabName: 'Customer' | 'Claims') => {
    setCustomerTabs((customerTabs) => ({
      data: (customerTabs.data ?? []).map((tab, index) =>
        index == activeTab - 1 ? { ...tab, activeTab: activeTabName } : tab,
      ),
    }));
  };
  const onEnter = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      navigate(`${window.location.pathname}?searchTerm=${seconadarySearchBoxTerm}`);
    }
  };
  const focusSecondarySearchBox = () => {
    if (seconadarySearchBoxRef.current) seconadarySearchBoxRef.current.focus();
  };

  return (
    <div className="mywork-tabs" data-theme="evolved">
      {quoteModalConfig.isOpen && (
        <QuoteModal open={true} state={quoteModalConfig.defaultState} onClose={() => quoteModalOnClose()} />
      )}
      <SearchInput
        labelVisual="Search customers"
        value={seconadarySearchBoxTerm}
        onChange={(event) => setSecondarySearchBoxTerm(event.target.value.trim())}
        placeholder="Search customers by name, email, or policy, claim or phone number"
        onClearClicked={() => setSecondarySearchBoxTerm('')}
        className="mywork-secondary-search-box"
        onKeyDown={(event) => onEnter(event)}
        innerRef={seconadarySearchBoxRef}
      />
      <Tabs activeTab={activeTab} noLeftIndent onTabActivated={onTabChange}>
        <Tab
          labelVisual={
            <>
              <IconSearch size="16" />
              Search Results
            </>
          }
        >
          <SearchResults focusSearch={() => focusSecondarySearchBox()} />
        </Tab>
        {customerTabs &&
          customerTabs.map((tab, index) => (
            <Tab
              className={tab.activeTab == 'Claims' ? 'mywork-tab-gutter' : ''}
              labelVisual={
                tab.name ? (
                  <>
                    <IconMyAccount size="16" />
                    {tab.name}
                    <IconClose
                      size="16"
                      className="customer-tab-close-icon"
                      onClick={(event) => closeTab(event, index)}
                    />
                  </>
                ) : (
                  <div className="mywork-tab-title">
                    <CircularProgressSpinner size="24" aria-label="Getting customer" />
                    <IconClose
                      size="16"
                      className="customer-tab-close-icon"
                      onClick={(event) => closeTab(event, index)}
                    />
                  </div>
                )
              }
            >
              <CustomerTab
                message={tab.message}
                activeTab={tab.activeTab}
                updateActiveTab={(activeTabName: 'Claims' | 'Customer') => updateActiveTab(activeTabName)}
                name={tab.name}
                policies={tab.policies}
                claims={tab.claims}
                billing={tab.billing}
              />
            </Tab>
          ))}
      </Tabs>
    </div>
  );
}
